import { runBulkDeleteWorkflow } from "@/action-dialog";
import { useDeleteCanvas } from "@/api/canvas";
import { useDeleteFileRecord, useWorkspaceTree } from "@/api/fileRecords";
import { useDeleteFlowSession } from "@/api/flow_sessions";
import { ROUTES } from "@/constants/routes";
import { useOpenChat } from "@/hooks/useOpenChat";
import { useRailSelection } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import {
  getWorkspacesRailSelectionCapabilities,
  toUnifiedItems,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/workspacesRailSelectionCapabilities";
import {
  useMultiSelectionObjectActionHandlers,
  useObjectActions,
} from "@/object-actions";
import { useAttachBulkTransferSubmenu } from "@/object-actions/transfer/useBulkTransferSubmenu";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { route } from "@/utils/route";
import type { Canvas, FileRecord, InboxItem } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/**
 * Bulk move/delete + selection-bar actions for Workspaces-rail multi-select.
 * Resolves selected row keys through the rail resource registry.
 */
export function useWorkspacesRailMultiSelectionActions() {
  const selection = useRailSelection();
  const navigate = useNavigate();
  const { openChat } = useOpenChat();
  const openFileRecord = useOpenFileRecord();
  const queryClient = useQueryClient();

  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete canvas"),
  });
  const deleteFileRecord = useDeleteFileRecord(undefined, {
    onError: () => toast.error("Failed to delete file"),
  });
  const deleteFlowSession = useDeleteFlowSession({
    onError: () => toast.error("Failed to delete chat"),
  });

  const selectedResources = useMemo(
    () => selection.getSelectedResources(),
    // getSelectedResources reads a ref keyed off the current selection set
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection.selectedIds, selection.selectionCount],
  );

  const selectedItems = useMemo(
    () => toUnifiedItems(selectedResources),
    [selectedResources],
  );

  const selectionCapabilities = useMemo(
    () => getWorkspacesRailSelectionCapabilities(selectedResources),
    [selectedResources],
  );

  // Folder picker needs the full workspace tree, not only selected files.
  const { data: workspaceFileRecords = [] } = useWorkspaceTree(
    selectionCapabilities.workspaceId ?? undefined,
  );

  const handleBulkDeleteCanvas = useCallback(
    async (canvas: Canvas) => {
      await deleteCanvas.mutateAsync(canvas.id);
    },
    [deleteCanvas],
  );

  const handleBulkDeleteFileRecord = useCallback(
    async (record: FileRecord) => {
      await deleteFileRecord.mutateAsync(record.id);
    },
    [deleteFileRecord],
  );

  const handleBulkDeleteChat = useCallback(
    async (chat: InboxItem) => {
      await deleteFlowSession.mutateAsync(chat.flow_session_id);
      queryClient.invalidateQueries({
        queryKey: ["workspace-flow-sessions", "recent"],
      });
    },
    [deleteFlowSession, queryClient],
  );

  const openBulkDelete = useCallback(() => {
    void runBulkDeleteWorkflow({
      items: selectedItems,
      onDeleteCanvas: handleBulkDeleteCanvas,
      onDeleteFileRecord: handleBulkDeleteFileRecord,
      onDeleteChat: handleBulkDeleteChat,
      onDeleted: () => selection.clear(),
    });
  }, [
    selectedItems,
    handleBulkDeleteCanvas,
    handleBulkDeleteFileRecord,
    handleBulkDeleteChat,
    selection,
  ]);

  const handleSelectionOpen = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const item = selectedItems[0]!;
    if (item.kind === "canvas") {
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: item.data.id }));
    } else if (item.kind === "file") {
      openFileRecord(item.data.id);
    } else if (item.kind === "chat") {
      openChat(item.data.flow_session_id, { source: "rail-selection" });
    }
  }, [selectedItems, navigate, openChat, openFileRecord]);

  const selectionKinds = useMemo(() => {
    const kinds = new Set<"canvas" | "chat" | "file" | "folder">();
    for (const item of selectedItems) {
      if (item.kind === "canvas") kinds.add("canvas");
      else if (item.kind === "chat") kinds.add("chat");
      else kinds.add("file");
    }
    return [...kinds];
  }, [selectedItems]);

  const multiSelectionHandlers = useMultiSelectionObjectActionHandlers({
    onOpen: handleSelectionOpen,
    onRename: () => {},
    // Bulk Move to is the nested picker attached below; nothing to open.
    onBulkMove: () => {},
    onDuplicate: () => {},
    onShare: () => {},
    onBulkDelete: openBulkDelete,
    canOpen: selectionCapabilities.canOpen,
    canRename: false,
    canMove: selectionCapabilities.canMove,
    canDuplicate: false,
    canShare: false,
    canDelete: selectionCapabilities.canDelete,
  });

  const actionState = {
    selectionSingle: selectionCapabilities.canOpen,
    canRenameSelection: false,
    canDuplicateSelection: false,
    canShare: false,
    hasChatsInSelection: selectionCapabilities.hasChats,
  };

  const { actions: selectionBarActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: selectionKinds },
    surface: "selection-bar",
    enabled: selection.selectionCount > 1,
    state: actionState,
    handlers: multiSelectionHandlers,
  });

  const { actions: leftRailMultiBase } = useObjectActions({
    kind: { type: "multi-selection", kinds: selectionKinds },
    surface: "left-rail",
    enabled: selection.selectionCount > 1,
    state: actionState,
    handlers: multiSelectionHandlers,
  });
  const leftRailMultiActions = useAttachBulkTransferSubmenu(
    leftRailMultiBase,
    selectedItems,
    selectionCapabilities.workspaceId ?? undefined,
    workspaceFileRecords,
    selection.clear,
  );

  return {
    selectionCount: selection.selectionCount,
    selectionBarActions,
    leftRailMultiActions,
    clear: selection.clear,
    clearIfOutside: selection.clearIfOutside,
    selectedItems,
  };
}
